import { useState } from "react";
import { adminHttp } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import { ArrowLeft, Save, X } from "lucide-react";

const TYPES = ["Villa", "Penthouse", "Mansion", "Apartment", "Estate"];
const STATUSES = ["For Sale", "For Rent", "Off-Market", "Sold"];

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function PropertyForm({ initial, onCancel, onSaved, mode }) {
  const [form, setForm] = useState(initial);
  const [featureInput, setFeatureInput] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm({ ...form, [k]: v });
  };

  const setNum = (k) => (e) => setForm({ ...form, [k]: parseInt(e.target.value || "0", 10) });

  const addFeature = () => {
    const f = featureInput.trim();
    if (!f) return;
    setForm({ ...form, features: [...(form.features || []), f] });
    setFeatureInput("");
  };

  const removeFeature = (i) =>
    setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug) {
      toast.error("Title and slug are required.");
      return;
    }
    if (!form.images?.length) {
      toast.error("Add at least one image.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "create") {
        await adminHttp.post("/admin/properties", form);
        toast.success("Property created.");
      } else {
        await adminHttp.put(`/admin/properties/${initial.slug}`, form);
        toast.success("Property updated.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      data-testid="admin-property-form"
      onSubmit={submit}
      className="space-y-10"
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          data-testid="admin-property-form-back"
          onClick={onCancel}
          className="inline-flex items-center gap-2 underline-label text-[#6a6259] hover:text-[#c5a059]"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <h2 className="font-display text-2xl text-[#1c1c1c]">
          {mode === "create" ? "New Property" : `Edit · ${initial.title}`}
        </h2>
      </div>

      {/* Basics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 border-t border-[#1c1c1c]/10 pt-8">
        <Field label="Title" required>
          <input
            data-testid="property-form-title"
            className="editorial-input"
            value={form.title}
            onChange={(e) => {
              const title = e.target.value;
              setForm({ ...form, title, slug: mode === "create" ? slugify(title) : form.slug });
            }}
            required
          />
        </Field>
        <Field label="Slug" required>
          <input
            data-testid="property-form-slug"
            className="editorial-input"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
            disabled={mode === "edit"}
            required
          />
        </Field>
        <Field label="Location">
          <input data-testid="property-form-location" className="editorial-input" value={form.location} onChange={set("location")} />
        </Field>
        <Field label="Area / Sub-community">
          <input data-testid="property-form-area" className="editorial-input" value={form.area} onChange={set("area")} />
        </Field>
        <Field label="Type">
          <select data-testid="property-form-type" className="editorial-input bg-transparent" value={form.type} onChange={set("type")}>
            {TYPES.map((t) => <option key={t} className="bg-[#ede5d5]" value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select data-testid="property-form-status" className="editorial-input bg-transparent" value={form.status} onChange={set("status")}>
            {STATUSES.map((s) => <option key={s} className="bg-[#ede5d5]" value={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      {/* Pricing & Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-x-8 gap-y-6 border-t border-[#1c1c1c]/10 pt-8">
        <Field label="Price AED">
          <input data-testid="property-form-price_aed" type="number" className="editorial-input" value={form.price_aed} onChange={setNum("price_aed")} />
        </Field>
        <Field label="Price USD">
          <input data-testid="property-form-price_usd" type="number" className="editorial-input" value={form.price_usd} onChange={setNum("price_usd")} />
        </Field>
        <Field label="Bedrooms">
          <input data-testid="property-form-bedrooms" type="number" className="editorial-input" value={form.bedrooms} onChange={setNum("bedrooms")} />
        </Field>
        <Field label="Bathrooms">
          <input data-testid="property-form-bathrooms" type="number" className="editorial-input" value={form.bathrooms} onChange={setNum("bathrooms")} />
        </Field>
        <Field label="Size (sqft)">
          <input data-testid="property-form-size_sqft" type="number" className="editorial-input" value={form.size_sqft} onChange={setNum("size_sqft")} />
        </Field>
      </div>

      {/* Description */}
      <div className="border-t border-[#1c1c1c]/10 pt-8">
        <Field label="Description">
          <textarea
            data-testid="property-form-description"
            rows={5}
            className="editorial-input resize-none"
            value={form.description}
            onChange={set("description")}
          />
        </Field>
      </div>

      {/* Features */}
      <div className="border-t border-[#1c1c1c]/10 pt-8">
        <div className="underline-label mb-3">Signature Features</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {(form.features || []).map((f, i) => (
            <span
              key={i}
              data-testid={`property-form-feature-${i}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#c5a059]/30 text-[#1c1c1c] text-xs"
            >
              {f}
              <button type="button" onClick={() => removeFeature(i)} className="text-[#6a6259] hover:text-[#c5a059]">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-3">
          <input
            data-testid="property-form-feature-input"
            className="editorial-input !py-2 flex-1"
            placeholder="e.g. Private Beach"
            value={featureInput}
            onChange={(e) => setFeatureInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
          />
          <button
            type="button"
            data-testid="property-form-feature-add"
            onClick={addFeature}
            className="px-5 py-2 border border-[#c5a059]/60 text-[#c5a059] uppercase text-[10px] tracking-[0.28em] hover:bg-[#c5a059] hover:text-black transition-colors"
          >
            Add
          </button>
        </div>
      </div>

      {/* Images */}
      <div className="border-t border-[#1c1c1c]/10 pt-8">
        <div className="underline-label mb-3">Photography</div>
        <ImageUploader
          value={form.images}
          onChange={(images) => setForm({ ...form, images })}
          testId="property"
        />
      </div>

      {/* Featured + Submit */}
      <div className="border-t border-[#1c1c1c]/10 pt-8 flex items-center justify-between">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            data-testid="property-form-featured"
            type="checkbox"
            checked={!!form.featured}
            onChange={set("featured")}
            className="w-4 h-4 accent-[#c5a059]"
          />
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#1c1c1c]">
            Featured on homepage
          </span>
        </label>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="property-form-cancel"
            onClick={onCancel}
            className="px-6 py-3 border border-[#c5a059]/30 text-[#6a6259] uppercase text-[10px] tracking-[0.32em] hover:text-[#1c1c1c]"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="property-form-submit"
            disabled={busy}
            className="inline-flex items-center gap-3 px-7 py-3 bg-[#c5a059] text-black uppercase text-[10px] tracking-[0.32em] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] transition-colors disabled:opacity-60"
          >
            <Save size={13} />
            {busy ? "Saving…" : mode === "create" ? "Create" : "Save Changes"}
          </button>
        </div>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="underline-label mb-3 text-[#6a6259]">
        {label}{required && <span className="text-[#c5a059] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
